/**
 * Page-level error state (APP-001 §12).
 *
 * Renders a typed, recoverable error view. Never displays raw stack traces,
 * Clerk JWTs, API keys, Stripe URLs, or internal error details. When a server
 * request ID is available it is shown in a copyable diagnostic area so the
 * user can include it in a support request.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, ShieldAlert } from '@/components/icons';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RequestId } from './RequestId';
import { isApiError, type ApiError } from '@/api/errors';
import { getAppLinks } from '@/config/links';

export interface ErrorStateProps {
  title?: string;
  description?: string;
  /** The thrown error. Unknown values degrade to a generic message. */
  error?: unknown;
  /** Optional retry control. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Optional extra actions (e.g. "Contact support"). */
  action?: ReactNode;
  icon?: 'warning' | 'shield';
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description,
  error,
  onRetry,
  retryLabel = 'Try again',
  action,
  icon = 'warning',
  className,
}: ErrorStateProps) {
  const apiError: ApiError | undefined = isApiError(error) ? error : undefined;
  const supportUrl = getAppLinks().support;
  const message = description ?? apiError?.message;

  const Icon = icon === 'shield' ? ShieldAlert : AlertTriangle;

  return (
    <Alert variant="destructive" className={className} data-testid="error-state">
      <Icon className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {message ? <p>{message}</p> : null}
        {apiError?.requestId ? <RequestId requestId={apiError.requestId} className="mt-3" /> : null}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {onRetry ? (
            <Button size="sm" variant="outline" onClick={onRetry}>
              {retryLabel}
            </Button>
          ) : null}
          {action ?? (
            <Button size="sm" variant="ghost" asChild>
              <a href={supportUrl} rel="noopener noreferrer">
                Contact support
              </a>
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}
